import { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const STATUSES = ["Active", "Draft", "Inactive"];

export default function AddSolutionModal({ open, onClose, onCreated, API }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("Draft");
  const [isCore, setIsCore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setName("");
    setSlug("");
    setDescription("");
    setStatus("Draft");
    setIsCore(false);
    setError("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/admin/solutions`, { name, slug: slug || undefined, description, status, is_core: isCore }, { withCredentials: true });
      onCreated();
      close();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" data-testid="add-solution-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 dark:bg-[#111C33]">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Add Solution</h2>
          <button type="button" onClick={close} data-testid="add-solution-close-btn" className="text-slate-500 dark:text-slate-400"><X size={18} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Solution Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="add-solution-name" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Internal Key / Slug</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-generated from name if left blank" data-testid="add-solution-slug" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="add-solution-description" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="add-solution-status" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label className="mt-6 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={isCore} onChange={(e) => setIsCore(e.target.checked)} data-testid="add-solution-core" /> Core Solution
            </label>
          </div>
        </div>
        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="add-solution-error">{error}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={close} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 dark:border-white/15 dark:text-slate-200">Cancel</button>
          <button type="button" onClick={submit} disabled={saving || !name} data-testid="add-solution-submit" className="rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
            {saving ? "Creating…" : "Create Solution"}
          </button>
        </div>
      </div>
    </div>
  );
}
